import React, { useState } from 'react';
import axios from 'axios';

const Register = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    preferredContact: 'email',
    companyName: '',
    companyAddress: '',
    experienceYears: '',
    hourlyRate: '',
    bio: '',
    qualifications: '',
    specialties: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const usStates = [
    { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
    { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
    { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
    { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
    { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
    { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
    { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
    { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
    { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
    { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
    { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
    { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
    { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
    { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
    { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
    { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
    { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }
  ];

  const experienceYearsOptions = [
    { value: '1', label: '1 year' },
    { value: '2', label: '2 years' },
    { value: '3', label: '3 years' },
    { value: '4', label: '4 years' },
    { value: '5', label: '5 years' },
    { value: '6', label: '6 years' },
    { value: '7', label: '7 years' },
    { value: '8', label: '8 years' },
    { value: '9', label: '9 years' },
    { value: '10+', label: '10+ years' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle phone number input - only numbers and format
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    // Format as (123) 456-7890
    if (value.length > 3 && value.length <= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else if (value.length > 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    }
    
    setFormData(prevState => ({
      ...prevState,
      phone: value
    }));
  };

  // Handle zip code separately to ensure it's only numbers
  const handleZipCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setFormData(prevState => ({
      ...prevState,
      zipCode: value
    }));
  };

  // Handle hourly rate input - format with $ symbol and allow decimals
  const handleHourlyRateChange = (e) => {
    let value = e.target.value.replace(/[^\d.]/g, '');
    
    // Remove extra decimal points
    const decimalParts = value.split('.');
    if (decimalParts.length > 2) {
      value = decimalParts[0] + '.' + decimalParts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (decimalParts.length === 2 && decimalParts[1].length > 2) {
      value = decimalParts[0] + '.' + decimalParts[1].slice(0, 2);
    }
    
    setFormData(prevState => ({
      ...prevState,
      hourlyRate: value
    }));
  };

  // Handle bio character count
  const handleBioChange = (e) => {
    const value = e.target.value;
    setFormData(prevState => ({
      ...prevState,
      bio: value
    }));
  };

  // Client-side validation
  const validateForm = () => {
    if (!formData.fullName.trim()) {
      return 'Full name is required';
    }
    
    if (!formData.email && !formData.phone) {
      return 'Either email or phone number is required';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    
    if (formData.phone && formData.phone.replace(/\D/g, '').length < 10) {
      return 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.zipCode || formData.zipCode.length !== 5) {
      return 'Valid 5-digit zip code is required';
    }
    
    if (!formData.city.trim()) {
      return 'City is required';
    }
    
    if (!formData.state) {
      return 'State is required';
    }
    
    if (!formData.address.trim()) {
      return 'Address is required';
    }
    
    // New field validations
    if (!formData.experienceYears) {
      return 'Experience years is required';
    }
    
    if (!formData.hourlyRate) {
      return 'Hourly rate is required';
    }
    
    if (!formData.bio.trim()) {
      return 'Bio is required';
    }
    
    if (formData.bio.length < 20) {
      return 'Bio must be at least 20 characters long';
    }
    
    if (formData.bio.length > 500) {
      return 'Bio must not exceed 500 characters';
    }
    
    if (!formData.qualifications.trim()) {
      return 'Qualifications are required';
    }
    
    if (!formData.specialties.trim()) {
      return 'Specialties are required';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const validationError = validateForm();
    if (validationError) {
      setSubmitMessage(`Error: ${validationError}`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // console.log('Sending registration data:', formData);
      
      // Use absolute URL for production
      // const API_BASE = process.env.NODE_ENV === 'development' 
      //   ? 'https://manage.ebookkeepings.com' 
      //   : '';
      // const API_BASE = 'https://manage.ebookkeepings.com';
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      
      const response = await axios.post(`${API_BASE}/api/bookkeepers/register`, formData);
      
      // console.log('Registration response:', response);
      
      if (response.data.success) {
        setSubmitMessage(response.data.message || 'Thank you for registering! Your listing is pending admin approval and will be available soon.');
        
        // Clear form on success
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          preferredContact: 'email',
          companyName: '',
          companyAddress: '',
          experienceYears: '',
          hourlyRate: '',
          bio: '',
          qualifications: '',
          specialties: ''
        });
      } else {
        setSubmitMessage(response.data.message || 'Registration completed but there was an issue.');
      }
    } catch (error) {
      console.error('Registration error details:', error);
      console.error('Error response:', error.response);
      
      if (error.response && error.response.data) {
        setSubmitMessage(`Error: ${error.response.data.message}`);
      } else {
        setSubmitMessage('Sorry, there was an error with your registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const bioCharacterCount = formData.bio.length;
  const isBioValid = bioCharacterCount >= 20 && bioCharacterCount <= 500;

  return (
    <div className="page register">
      <div className="container">
        <h1>Register as a Bookkeeper</h1>
        <p>Join our network of professional bookkeepers and get connected with clients in your area.</p>
        <p><strong>Note:</strong> All registrations require admin approval before appearing in search results.</p>
        <p><em>You must provide either an email address or phone number.</em></p>
        
        <form className="registration-form" onSubmit={handleSubmit}>
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Error:') || submitMessage.includes('error') ? 'error' : 'success'}`}>
              {submitMessage}
            </div>
          )}
          
          <h3>Personal Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="(123) 456-7890"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="zipCode">Zip Code *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleZipCodeChange}
                maxLength="5"
                pattern="[0-9]{5}"
                title="5-digit zip code"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="state">State *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select State</option>
                {usStates.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="address">Street Address *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main Street"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Preferred Contact Method *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="preferredContact"
                  value="email"
                  checked={formData.preferredContact === 'email'}
                  onChange={handleChange}
                  disabled={!formData.email} // Disable if no email provided
                />
                Email {!formData.email && '(requires email)'}
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="preferredContact"
                  value="phone"
                  checked={formData.preferredContact === 'phone'}
                  onChange={handleChange}
                  disabled={!formData.phone} // Disable if no phone provided
                />
                Phone {!formData.phone && '(requires phone number)'}
              </label>
            </div>
          </div>
          
          <h3>Professional Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="experienceYears">Years of Experience *</label>
              <select
                id="experienceYears"
                name="experienceYears"
                value={formData.experienceYears}
                onChange={handleChange}
                required
              >
                <option value="">Select Years</option>
                {experienceYearsOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="hourlyRate">Hourly Rate ($) *</label>
              <div className="input-with-symbol">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  id="hourlyRate"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleHourlyRateChange}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="bio">
              Professional Bio * 
              <span className={`character-count ${isBioValid ? 'valid' : 'invalid'}`}>
                ({bioCharacterCount}/500 characters)
              </span>
            </label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleBioChange}
              rows="4"
              placeholder="Tell us about your professional background, expertise, and approach to bookkeeping..."
              minLength="20"
              maxLength="500"
              required
            />
            {bioCharacterCount < 20 && (
              <small className="error-text">Bio must be at least 20 characters long</small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="qualifications">Qualifications *</label>
            <input
              type="text"
              id="qualifications"
              name="qualifications"
              value={formData.qualifications}
              onChange={handleChange}
              placeholder="CPA, QuickBooks Certified, etc. (comma separated)"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="specialties">Specialties *</label>
            <input
              type="text"
              id="specialties"
              name="specialties"
              value={formData.specialties}
              onChange={handleChange}
              placeholder="Small Business, Tax Preparation, etc. (comma separated)"
              required
            />
          </div>
          
          <h3>Company Information (Optional)</h3>
          
          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="companyAddress">Company Address</label>
            <input
              type="text"
              id="companyAddress"
              name="companyAddress"
              value={formData.companyAddress}
              onChange={handleChange}
            />
          </div>
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting || (!formData.email && !formData.phone) || !isBioValid}
          >
            {isSubmitting ? 'Registering...' : 'Register as Bookkeeper'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;